import type { AmplienceTopLevelNavigationCategory } from '../types';

import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import type { Configuration } from '~/utilities/graphql/codegen';

/**
 * Whether the content has at least one category the TLN would render (same rule as
 * `useTopLevelNavigationCategories`: a category needs a label).
 */
export const hasTopLevelNavigationCategories = (
  categories?: AmplienceTopLevelNavigationCategory[] | null
): boolean => !!categories?.some(category => !!category.label);

export type TopLevelNavigationEnabled = {
  /** Desktop TLN: `topNavigationEnabled` switch plus renderable content. */
  desktop: boolean;
  /**
   * Mobile TLN drawer: `topNavigationMobileEnabled` switch, independent from the desktop one, plus
   * renderable content. When false, mobile keeps the existing side navigation.
   */
  mobile: boolean;
};

/**
 * Whether the desktop TLN and the mobile TLN drawer are enabled (switch + renderable content).
 * Pure counterpart of `useTopLevelNavigationEnabled`, for callers outside `StaticContextProvider`.
 */
export const getTopLevelNavigationEnabled = (
  {
    topNavigationEnabled,
    topNavigationMobileEnabled,
  }: Pick<Configuration, 'topNavigationEnabled' | 'topNavigationMobileEnabled'>,
  categories?: AmplienceTopLevelNavigationCategory[] | null
): TopLevelNavigationEnabled => {
  const hasCategories = hasTopLevelNavigationCategories(categories);

  return {
    desktop: !!topNavigationEnabled && hasCategories,
    mobile: !!topNavigationMobileEnabled && hasCategories,
  };
};

/** Whether the desktop TLN and the mobile TLN drawer are enabled (switch + renderable content). */
export const useTopLevelNavigationEnabled = (): TopLevelNavigationEnabled => {
  const { configuration, topLevelNavigationContent } = useStaticContext();

  return getTopLevelNavigationEnabled(configuration, topLevelNavigationContent?.categories);
};

/** The mobile drawer is in use: mobile switch on and the viewport is below desktop (`lg`). */
export const useMobileTopLevelNavigationActive = (): boolean => {
  const { isDesktop } = useMediaQueryContext();

  return useTopLevelNavigationEnabled().mobile && !isDesktop;
};

/** TLN replaces the side navigation: desktop TLN on desktop, or the mobile drawer below `lg`. */
export const useTopLevelNavigationActive = (): boolean => {
  const { isDesktop } = useMediaQueryContext();
  const { desktop, mobile } = useTopLevelNavigationEnabled();

  return isDesktop ? desktop : mobile;
};

/**
 * Whether the mobile TLN drawer owns the header overlays: the locale selector (auto-open and the
 * only rendered modal) and the message box panel. Below `lg` with the drawer enabled. With the
 * drawer switched off it never does (`false`, whatever the viewport). Otherwise `null` until the
 * viewport is known, so neither the drawer nor the header acts on a guessed viewport.
 */
export const useMobileTopLevelNavigationOwnsOverlays = (): boolean | null => {
  const { breakpoint, isDesktop } = useMediaQueryContext();
  const { mobile } = useTopLevelNavigationEnabled();

  if (!mobile) {
    return false;
  }

  return breakpoint === undefined ? null : !isDesktop;
};
